import type { BannerDetails, BannerExpandedDetails, BannerVariant, BannerVisibilityToken } from 'models/banner';
import type { BannerState } from 'stores/zustand/bannerStore';
import config from 'utils/config';
import { getOutsideTradingHoursMessage } from 'utils/time';

const DISPLAY_DATE_TIME_MASK = 'cccc, MM/dd h:mm a ZZZZ';
const DISPLAY_TIME_MASK = 'h:mm a ZZZZ';

export function normalizeManualBanners(data: BannerExpandedDetails | null | undefined): BannerDetails[] {
    const webBanners = data?.banners?.filter(
        (banner) => banner.platforms?.includes('web') || !banner.platforms?.length,
    );

    if (webBanners?.length) return webBanners;
    if (!data?.content || !data.visibilityTokens?.length) return [];

    return [
        {
            content: data.content,
            visibilityTokens: data.visibilityTokens,
            position: data.position ?? 'top',
            variant: data.variant,
            platforms: data.platforms,
        },
    ];
}

/**
 * Falls back to the legacy env-configured banner (`NEXT_PUBLIC_MANUAL_BANNER_*`) when
 * product-info returned nothing — whether because it is genuinely empty, errored, or 404'd.
 *
 * Every path that produces the manual banner set has to apply this, or the paths disagree
 * and each disagreement is a layout shift: seeding without it leaves a configured banner out
 * of the initial HTML for the mount to insert, and polling without it drops a banner the
 * mount just put up. Keep `prefetchManualBanners`, `initialize`, and `fetchManualBanners`
 * routed through here rather than repeating the condition.
 */
export function withConfiguredBannerFallback(banners: BannerDetails[]): BannerDetails[] {
    if (banners.length > 0) return banners;
    if (!config.manualBannerContent || !config.manualBannerVisibilityTokens.length || !config.manualBannerPosition) {
        return banners;
    }

    return [
        {
            content: config.manualBannerContent,
            visibilityTokens: config.manualBannerVisibilityTokens,
            position: config.manualBannerPosition,
        },
    ];
}

function isBannerVisible(pathName: string, banner: BannerDetails, isLoggedIn: boolean): boolean {
    const reliesOnBackend =
        isLoggedIn ||
        pathName.includes('events') ||
        pathName.includes('markets') ||
        pathName.includes('category') ||
        pathName.includes('live') ||
        pathName.includes('browse') ||
        pathName.includes('account') ||
        pathName.includes('sign-up') ||
        pathName.includes('password-forgot') ||
        pathName.includes('reset-password') ||
        (pathName.includes('market') && !pathName.includes('market-data')) ||
        pathName.includes('sign-in');
    const isSignup = pathName.includes('sign-up');
    const isTransfers = pathName.includes('/account/transfers');
    const isLanding = pathName === '/';

    const { visibilityTokens } = banner;
    const isVisible =
        visibilityTokens?.includes('all') ||
        (visibilityTokens?.includes('app') && reliesOnBackend) ||
        (visibilityTokens?.includes('logged-in-app') && reliesOnBackend && isLoggedIn && !isSignup) ||
        (visibilityTokens?.includes('landing') && isLanding) ||
        (visibilityTokens?.includes('transfers') && isTransfers) ||
        (visibilityTokens?.includes('logged-out') && !isLoggedIn);

    return !!banner.content && !!isVisible;
}

export function getVisibleBanner(
    pathName: string,
    banners: Array<BannerDetails>,
    isLoggedIn: boolean,
): BannerDetails | null {
    const visibleBanners = banners.filter((banner) => isBannerVisible(pathName, banner, isLoggedIn));

    if (visibleBanners.length === 0) {
        return null;
    }

    // Visibility priority:
    // P0: landing and transfers
    // P1: logged-in-app
    // P2: logged-out and app
    // P3: all
    const priorityOrder: BannerVisibilityToken[] = [
        'landing',
        'transfers',
        'logged-in-app',
        'logged-out',
        'app',
        'all',
    ];
    visibleBanners.sort((a, b) => {
        const aPriority = Math.min(
            ...(a.visibilityTokens?.map((token) => priorityOrder.indexOf(token)) ?? [priorityOrder.length]),
        );
        const bPriority = Math.min(
            ...(b.visibilityTokens?.map((token) => priorityOrder.indexOf(token)) ?? [priorityOrder.length]),
        );
        return aPriority - bPriority;
    });

    return visibleBanners[0] || null;
}

/**
 * Picks the banner to render while auth state is still unknown — server-side and on the
 * first client paint, where `useAuthStore` reports its logged-out default before it has
 * initialized from browser storage.
 *
 * Returns a banner only when both auth states select the *same* one, so the initial HTML
 * can never show a banner that hydration then takes away, swaps, or inserts — each of
 * which reintroduces the top-of-page shift.
 *
 * Comparing the finished selections rather than filtering candidates first is what makes
 * that hold. Priority is decided across the whole visible set, so dropping auth-scoped
 * candidates up front would happily serve a lower-priority `all` banner even when a
 * signed-in viewer's real winner is a `logged-in-app` one — hydration would then swap the
 * message, and shift the page whenever the two wrap to different heights.
 */
export function getAuthInvariantVisibleBanner(pathName: string, banners: Array<BannerDetails>): BannerDetails | null {
    const loggedOutBanner = getVisibleBanner(pathName, banners, false);

    // Identity is the right comparison: `getVisibleBanner` returns an element of `banners`,
    // and two distinct entries with equal copy are still separate banners.
    return loggedOutBanner === getVisibleBanner(pathName, banners, true) ? loggedOutBanner : null;
}

/**
 * Derives automated banner details from banner state and exchange store.
 * Call at the consumption site rather than storing redundant derived state.
 */
export const getAutomatedBannerDetails = (
    bannerState: Pick<
        BannerState,
        | 'maintenanceMode'
        | 'maintenanceStart'
        | 'maintenanceEnd'
        | 'queryExchangeDown'
        | 'currentlyInTradingHours'
        | 'tradingHoursStart'
        | 'tradingHoursEnd'
    >,
    exchangeActive: boolean,
    tradingActive: boolean,
): BannerDetails => {
    let displayString: string | undefined;
    let severity: BannerVariant | undefined;

    const maintenanceStartString = bannerState.maintenanceStart?.toFormat(DISPLAY_DATE_TIME_MASK);
    const maintenanceEndString = bannerState.maintenanceEnd?.toFormat(DISPLAY_DATE_TIME_MASK);
    const tradingHoursStartString = bannerState.tradingHoursStart?.toFormat(DISPLAY_TIME_MASK);
    const tradingHoursEndString = bannerState.tradingHoursEnd?.toFormat(DISPLAY_TIME_MASK);

    if (bannerState.maintenanceMode === 'on') {
        displayString = `Kalshi is currently under maintenance${
            maintenanceStartString !== undefined ? ` from ${maintenanceStartString}` : ''
        }${maintenanceEndString !== undefined ? `, until ${maintenanceEndString}` : ''}.`;
        severity = 'severe';
    } else if (bannerState.queryExchangeDown) {
        displayString = `We're having some issues on our end. Information that you see may be sparse or out of date.`;
        severity = 'warning';
    } else if (bannerState.maintenanceMode === 'notice' && maintenanceStartString !== undefined) {
        displayString = `Due to planned maintenance Kalshi will be unavailable from ${maintenanceStartString}${
            maintenanceEndString !== undefined ? `, to ${maintenanceEndString}` : ''
        }.`;
        severity = 'warning';
    } else if (
        !bannerState.currentlyInTradingHours &&
        tradingHoursStartString !== undefined &&
        tradingHoursEndString !== undefined
    ) {
        displayString = getOutsideTradingHoursMessage();
        severity = 'warning';
    } else if (!exchangeActive) {
        displayString = `The Kalshi Exchange is temporarily paused. Orders and transactions cannot be placed or modified.`;
        severity = 'warning';
    } else if (!tradingActive) {
        displayString = `The Kalshi Exchange is temporarily paused.`;
        severity = 'warning';
    }

    return {
        content: displayString ?? '',
        variant: severity,
    };
};
